import { reactive, computed } from 'vue';

function normalizeString(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function parseFriendlyFloat(value) {
  if (value === null || value === undefined) return null;
  let str = String(value).trim();
  if (str === '') return null;

  // If it's a standard number format without commas/dots, parse it
  if (/^-?\d+$/.test(str)) {
    return parseInt(str, 10);
  }

  // If it has both dots and commas (e.g., 1.234,56 or 1,234.56)
  if (str.includes('.') && str.includes(',')) {
    if (str.indexOf('.') < str.indexOf(',')) {
      // Portuguese/German style: 1.234,56
      str = str.replace(/\./g, '').replace(',', '.');
    } else {
      // US style: 1,234.56
      str = str.replace(/,/g, '');
    }
  } else if (str.includes(',')) {
    // If it only has comma: 1234,56
    str = str.replace(',', '.');
  } else if (str.includes('.')) {
    // If it has a dot followed by exactly 3 digits, treat as thousands separator (e.g. 1.250)
    if (/^\d{1,3}(\.\d{3})+$/.test(str)) {
      str = str.replace(/\./g, '');
    }
  }

  const parsed = parseFloat(str);
  return isNaN(parsed) ? null : parsed;
}

function isEmptyFilter(value) {
  return normalizeString(value) === '';
}

function extractValues(item, paths) {
  const results = [];
  if (!item || !paths?.length) return results;

  const walk = (current, segments) => {
    // PROTEÇÃO: Se o nó atual for nulo ou indefinido, aborta imediatamente para evitar lixo no mapeamento (como o número '0')
    if (current === undefined || current === null) return;
    
    if (!segments.length) {
      results.push(current);
      return;
    }

    const [segment, ...rest] = segments;
    const isArraySegment = segment.endsWith('[]');
    const key = isArraySegment ? segment.slice(0, -2) : segment;

    if (isArraySegment) {
      const next = current[key];
      // Garante que só vai iterar se for de fato um array populado
      if (Array.isArray(next) && next.length > 0) {
        next.forEach(entry => walk(entry, rest));
      }
      return;
    }

    walk(current[key], rest);
  };

  paths.forEach(path => walk(item, path.split('.')));
  return results;
}

function collectUniqueValues(list, filters, ignoredKey, paths) {
  const values = new Set();

  (list || []).forEach(item => {
    if (!matchesFilters(item, filters, [ignoredKey])) return;

    extractValues(item, paths).forEach(value => {
      const normalized = normalizeString(value);
      // Evita Strings contendo apenas '0' gerados por caminhos inválidos ou mapeamentos numéricos indesejados nas colunas de texto
      if (normalized !== '' && normalized !== '0') {
        values.add(normalized);
      }
    });
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function exactMatcher(item, paths, value) {
  if (isEmptyFilter(value)) return true;
  const needle = normalizeString(value);
  return extractValues(item, paths).some(option => normalizeString(option) === needle);
}

function numericMatcher(item, paths, value) {
  if (isEmptyFilter(value)) return true;
  
  const filterVal = parseFriendlyFloat(value);
  if (filterVal === null) return false;
  
  return extractValues(item, paths).some(option => {
    if (option === null || option === undefined) return false;
    const optionVal = parseFriendlyFloat(option);
    if (optionVal === null) return false;
    
    return Math.abs(optionVal - filterVal) < 0.0001;
  });
}

function booleanMatcher(item, paths, value) {
  // Se o checkbox "Somente com retornos" estiver desmarcado (false), permite a exibição do registro
  if (!value) return true;
  
  // Se o checkbox estiver marcado (true), valida se oldServices é um array válido e possui itens que não estão ocultos
  return Array.isArray(item?.oldServices) && item.oldServices.length > 0 && item.oldServices.some(ret => !ret?._hidden);
}

const filterDefinitions = [
  { key: 'qra', paths: ['oneService.codigoQRA', 'oldServices[].codigoQRA'], matcher: exactMatcher },
  { key: 'viatura', paths: ['oneService.codigoViatura', 'oldServices[].codigoViatura'], matcher: exactMatcher },
  { key: 'modulo', paths: ['oneService.nomeEquipamento', 'oldServices[].nomeEquipamento'], matcher: exactMatcher },
  { key: 'perfil', paths: ['oneService.nomePortal', 'oldServices[].nomePortal'], matcher: exactMatcher },
  { key: 'observacao', paths: ['oneService.observacao', 'oldServices[].observacao'], matcher: exactMatcher },
  { key: 'valor', paths: ['oneService.valor', 'oldServices[].valor'], matcher: numericMatcher },
  { key: 'hasReturns', paths: [], matcher: booleanMatcher }, // Atualizado para seguir a assinatura padrão de execução dos matchers
];

function matchesFilters(item, filters, ignoredKeys = []) {
  return filterDefinitions.every(def => {
    if (ignoredKeys.includes(def.key)) return true;
    return def.matcher(item, def.paths, filters[def.key]);
  });
}

export function useServiceFilters(originalServices) {
  const filters = reactive({
    qra: '',
    viatura: '',
    modulo: '',
    perfil: '',
    observacao: '',
    valor: '',
    hasReturns: false,
  });

  const uniqueQras = computed(() => collectUniqueValues(originalServices.value || [], filters, 'qra', ['oneService.codigoQRA', 'oldServices[].codigoQRA']));
  const uniqueViaturas = computed(() => collectUniqueValues(originalServices.value || [], filters, 'viatura', ['oneService.codigoViatura', 'oldServices[].codigoViatura']));
  const uniqueModulos = computed(() => collectUniqueValues(originalServices.value || [], filters, 'modulo', ['oneService.nomeEquipamento', 'oldServices[].nomeEquipamento']));
  const uniquePerfis = computed(() => collectUniqueValues(originalServices.value || [], filters, 'perfil', ['oneService.nomePortal', 'oldServices[].nomePortal']));
  const uniqueObservacoes = computed(() => collectUniqueValues(originalServices.value || [], filters, 'observacao', ['oneService.observacao', 'oldServices[].observacao']));

  const filteredServices = computed(() => {
    const list = originalServices.value || [];
    return list.filter(item => matchesFilters(item, filters));
  });

  function clearFilters() {
    Object.keys(filters).forEach(key => {
      filters[key] = typeof filters[key] === 'boolean' ? false : '';
    });
  }

  return {
    filters,
    uniqueQras,
    uniqueViaturas,
    uniqueModulos,
    uniquePerfis,
    uniqueObservacoes,
    filteredServices,
    clearFilters,
  };
}